import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Compass, MapPin, Heart, User, Search, Sun, Camera, Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LANGS } from "@/i18n";

function LangSelector() {
  const { lang, setLang } = useApp();
  return (
    <div className="flex items-center rounded-full bg-sand p-0.5" data-testid="lang-selector">
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${
            lang === l.code ? "bg-white text-terracotta shadow-sm" : "text-ink-muted hover:text-ink"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Layout({ children }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const onMap = location.pathname === "/map";

  const nav = [
    { to: "/", icon: Compass, label: t("discover"), testid: "nav-item-discover", exact: true },
    { to: "/map", icon: MapPin, label: t("map"), testid: "nav-item-map" },
    { to: "/favorites", icon: Heart, label: t("favorites"), testid: "nav-item-favorites" },
    { to: "/community", icon: Camera, label: t("community"), testid: "nav-item-community" },
    { to: "/profile", icon: User, label: t("profile"), testid: "nav-item-profile" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border/60">
        <div className="mx-auto max-w-2xl px-4 h-14 flex items-center justify-between gap-3">
          <button data-testid="logo-home" onClick={() => navigate("/")} className="flex items-center gap-2 shrink-0">
            <span className="hidden h-8 w-8 place-items-center rounded-xl bg-terracotta text-white shadow-sm min-[360px]:grid">
              <Sun className="h-4.5 w-4.5" strokeWidth={2.5} />
            </span>
            <span className="font-heading text-lg font-extrabold tracking-tight text-ink">Vale<span className="text-terracotta">Vale</span></span>
          </button>
          <div className="flex items-center gap-2">
            <LangSelector />
            <button data-testid="header-assistant-button" title={t("ai_title")} aria-label={t("ai_title")} onClick={() => navigate("/assistant")} className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-terracotta-light text-terracotta transition-colors hover:bg-sand"><Sparkles className="h-4 w-4" /></button>
            <button
              data-testid="header-search-btn"
              onClick={() => navigate("/explore")}
              className="grid h-9 w-9 place-items-center rounded-full bg-sea text-white hover:bg-sea-hover transition-colors"
              aria-label={t("search_placeholder")}
            >
              <Search className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className={onMap ? "" : "mx-auto max-w-2xl px-4 pb-28 pt-4"}>{children}</main>

      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 inset-x-0 z-50 backdrop-blur-xl bg-white/90 border-t border-border rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
      >
        <div className="mx-auto max-w-2xl px-2 h-[68px] grid grid-cols-5">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${
                    isActive ? "text-terracotta" : "text-ink-muted"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="h-5 w-5" strokeWidth={isActive ? 2.6 : 2} />
                    {n.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
